import { ORDER_STATUS_FILTERS } from "@bcc/components/orders/constants";
import { setOrdersFilterBarTestId } from "@bcc/components/orders/orders-filter-bar/orders-filter-bar.utils";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import { setSidebarNavTourId, TOUR_IDS } from "../tour-ids";
import type { FeatureTour, TourStepListItem } from "../tour.types";
import { DATA_TABLE_ROW_SELECTOR, testIdSelector, tourTargetSelector } from "../tour.utils";

// Vendors with carrier accounts are redirected from /orders to the
// marketplace-scoped variant, so both must count as the orders page.
const MARKETPLACE_ORDERS_PATTERN = `${ROUTE_PATHS.MARKETPLACE}/:carrierAccountId/orders`;

const ORDERS_TABLE = tourTargetSelector(TOUR_IDS.orders.table);

// What each status chip means, derived from the real chip config so labels and
// dot colours always match the filter bar; only the explanations live in tour copy.
const ORDER_STATUS_LIST: TourStepListItem[] = ORDER_STATUS_FILTERS.map((filter) => ({
  labelKey: filter.labelKey,
  descriptionKey: `tour.steps.orders.searchFilters.statuses.${filter.key}`,
  dotClassName: filter.dotColor,
}));

export const ordersTour: FeatureTour = {
  id: "orders",
  titleKey: "tour.features.orders.title",
  order: 2,
  requiredModule: "orders",
  requiredPage: "orders.view",
  steps: [
    {
      id: "orders.nav",
      route: ROUTE_PATHS.ORDERS,
      alternateRoutePatterns: [MARKETPLACE_ORDERS_PATTERN],
      target: tourTargetSelector(setSidebarNavTourId("orders")),
      titleKey: "tour.steps.orders.nav.title",
      bodyKey: "tour.steps.orders.nav.body",
      placement: "right",
    },
    {
      id: "orders.search-filters",
      route: ROUTE_PATHS.ORDERS,
      alternateRoutePatterns: [MARKETPLACE_ORDERS_PATTERN],
      target: testIdSelector(setOrdersFilterBarTestId("container")),
      titleKey: "tour.steps.orders.searchFilters.title",
      bodyKey: "tour.steps.orders.searchFilters.body",
      list: ORDER_STATUS_LIST,
      placement: "bottom",
    },
    {
      id: "orders.table",
      showsSampleData: true,
      route: ROUTE_PATHS.ORDERS,
      alternateRoutePatterns: [MARKETPLACE_ORDERS_PATTERN],
      target: ORDERS_TABLE,
      // Base text is the generic version (no batching) for vendors whose
      // fulfilment happens outside BCC (e.g. ShipStation roasters); the
      // override upgrades it for vendors who can actually create batches.
      titleKey: "tour.steps.orders.table.title",
      bodyKey: "tour.steps.orders.table.body",
      placement: "top",
      emptyState: {
        detectTarget: `${ORDERS_TABLE} ${DATA_TABLE_ROW_SELECTOR}`,
        bodyKey: "tour.steps.orders.table.emptyBody",
      },
      textOverrides: [
        {
          requiredModule: "batch",
          titleKey: "tour.steps.orders.table.batching.title",
          bodyKey: "tour.steps.orders.table.batching.body",
          emptyStateBodyKey: "tour.steps.orders.table.batching.emptyBody",
        },
      ],
    },
    {
      id: "orders.show-summary",
      route: ROUTE_PATHS.ORDERS,
      alternateRoutePatterns: [MARKETPLACE_ORDERS_PATTERN],
      target: tourTargetSelector(TOUR_IDS.orders.showSummary),
      titleKey: "tour.steps.orders.showSummary.title",
      bodyKey: "tour.steps.orders.showSummary.body",
      placement: "bottom",
    },
    {
      id: "orders.export",
      route: ROUTE_PATHS.ORDERS,
      alternateRoutePatterns: [MARKETPLACE_ORDERS_PATTERN],
      target: tourTargetSelector(TOUR_IDS.orders.export),
      titleKey: "tour.steps.orders.export.title",
      bodyKey: "tour.steps.orders.export.body",
      placement: "bottom",
      requiredPage: "orders.export",
    },
  ],
};
